import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAdminEvents, useDeleteEvent } from '../../../hooks/useEvents';
import { useToast } from '../../../hooks/useToast';
import { formatDate } from '../../../utils/formatters';
import Button from '../../../components/common/Button';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import Loading from '../../../components/common/Loading';
import ErrorMessage from '../../../components/common/ErrorMessage';
import Pagination from '../../../components/common/Pagination';

const STATUS_BADGE = {
  draft: { label: 'Rascunho', className: 'bg-gray-100 text-gray-700' },
  published: { label: 'Publicado', className: 'bg-green-100 text-green-700' },
};

const StatusBadge = ({ status }) => {
  const badge = STATUS_BADGE[status] || STATUS_BADGE.draft;

  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
};

const EventDate = ({ event }) => (
  <>
    {formatDate(event.startsAt, 'dd/MM/yyyy')}
    {!event.allDay && <span className="text-gray-400"> · {formatDate(event.startsAt, 'HH:mm')}</span>}
  </>
);

// Compactas (só ícone) na tabela; com texto e largura cheia nos cartões.
const EventActions = ({ event, onDelete, labeled = false }) => {
  const cardButton =
    'flex-1 inline-flex items-center justify-center gap-1 rounded border border-gray-200 py-2 text-sm font-medium';

  return (
    <>
      <Link
        to={`/admin/calendario/${event.id}`}
        aria-label={`Editar ${event.title}`}
        className={
          labeled
            ? `${cardButton} text-brand-brown`
            : 'p-2 text-gray-500 hover:text-brand-brown transition-colors'
        }
      >
        <Pencil className="h-4 w-4" />
        {labeled && 'Editar'}
      </Link>
      <button
        type="button"
        aria-label={`Excluir ${event.title}`}
        onClick={() => onDelete(event)}
        className={
          labeled ? `${cardButton} text-red-600` : 'p-2 text-gray-500 hover:text-red-600 transition-colors'
        }
      >
        <Trash2 className="h-4 w-4" />
        {labeled && 'Excluir'}
      </button>
    </>
  );
};

const EventsList = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null, title: '' });

  const { data, isLoading, error, refetch } = useAdminEvents({
    status: statusFilter || undefined,
    page,
  });
  const deleteEvent = useDeleteEvent();
  const toast = useToast();

  const events = data?.data || [];

  // Ao excluir o único evento da última página, volta para a anterior.
  useEffect(() => {
    if (data && events.length === 0 && page > 1) {
      setPage((p) => p - 1);
    }
  }, [data, events.length, page]);

  const closeDialog = () => setConfirmDialog({ isOpen: false, id: null, title: '' });
  const askDelete = (event) => setConfirmDialog({ isOpen: true, id: event.id, title: event.title });

  const handleConfirm = async () => {
    const { id } = confirmDialog;
    closeDialog();

    try {
      await deleteEvent.mutateAsync(id);
      toast.success('Evento removido com sucesso!');
    } catch (err) {
      toast.error('Erro ao remover evento: ' + err.message);
    }
  };

  if (isLoading) {
    return <Loading text="Carregando eventos..." />;
  }

  if (error) {
    return <ErrorMessage message={error.message} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendário</h1>
          <p className="text-gray-600 mt-1">Gerencie os eventos da associação</p>
        </div>

        <Link to="/admin/calendario/novo">
          <Button variant="primary">
            <Plus className="h-4 w-4 mr-2" />
            Novo Evento
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <label htmlFor="statusFilter" className="label">
          Status
        </label>
        <select
          id="statusFilter"
          className="input max-w-xs"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos</option>
          <option value="draft">Rascunho</option>
          <option value="published">Publicado</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {events.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDays className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-4 text-gray-500">Nenhum evento encontrado.</p>
          </div>
        ) : (
          <>
            {/* Celular: cartões. Na tabela, Editar/Excluir ficavam fora da tela. */}
            <ul className="md:hidden divide-y divide-gray-200" data-testid="events-cards">
              {events.map((event) => (
                <li key={event.id} className="p-4 space-y-3" data-testid={`event-card-${event.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <span
                        className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: event.color || '#B47330' }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{event.title}</p>
                        <p className="text-xs text-gray-500">
                          <EventDate event={event} />
                        </p>
                        {event.location && <p className="text-xs text-gray-500">{event.location}</p>}
                      </div>
                    </div>
                    <StatusBadge status={event.status} />
                  </div>

                  <div className="flex gap-2">
                    <EventActions event={event} onDelete={askDelete} labeled />
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto" data-testid="events-table">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Evento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Local
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="sticky right-0 bg-gray-50 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {events.map((event) => {
                    return (
                      <tr key={event.id} className="group hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span
                              className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: event.color || '#B47330' }}
                            />
                            <span className="text-sm font-medium text-gray-900">{event.title}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <EventDate event={event} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{event.location || '—'}</td>
                        <td className="px-6 py-4">
                          <StatusBadge status={event.status} />
                        </td>
                        {/* Coluna fixa: as ações ficavam fora da área visível. */}
                        <td className="sticky right-0 bg-white group-hover:bg-gray-50 px-6 py-4 text-right shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                          <div className="flex items-center justify-end gap-2">
                            <EventActions event={event} onDelete={askDelete} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        <Pagination
          page={data?.page || 1}
          pageSize={data?.pageSize || 15}
          total={data?.total || 0}
          onPageChange={setPage}
        />
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        title="Excluir evento"
        message={`Tem certeza que deseja excluir "${confirmDialog.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        variant="destructive"
      />
    </div>
  );
};

export default EventsList;
